/**
 * Mensajes, lógica pura de la pantalla (docs/activa/FASE-3-PLAN.md, «Mensajes»): el borrador de cada grupo y cómo
 * cambia, el costo estimado (el mismo cálculo que el backend, backend/src/modules/mensajes/util/costo.util.ts) y el
 * HTML del mail para leer.
 *
 * Cambiar la oferta o el mínimo, «Otra versión» y «Volver a la sugerencia» vuelven a armar el texto (el anterior queda
 * para «Recuperar mi texto», un paso); cambiar el canal o si va, no lo tocan.
 *
 * Por el WhatsApp oficial no sale el texto sino una plantilla de Meta (R3, 29/10/2026): el grupo guarda cuál y qué dato
 * va en cada variable. Dos grupos con la misma plantilla y el mismo mapeo salen en la misma campaña del oficial.
 */

import type { TonoMarca } from "../../branding/types";
import type { DescuentoVigente } from "../../descuentos/types";
import { num } from "../../campana-nueva/utils/format";
import type { Cupon } from "../../cupones/types";
import type { MapeoVariables } from "../../wa-oficial/types";
import type { Canal, CuponDeLaVista, GrupoDeMensajes, Mensaje, MensajeParaGuardar, Sugerencia, VistaDeMensajes } from "../types";
import { componerMensaje, VERSIONES } from "./textos";

export const rutaMensajes = (id: string) => `/activa/segmentaciones/${encodeURIComponent(id)}/mensajes`;

export const NOMBRE_CANAL: Record<Canal, string> = { email: "Email", whatsapp: "WhatsApp" };

/* ── El costo ────────────────────────────────────────────────────────────────────────────────── */

/** reciben × compra semanal × uso del cupón × ticket × %, redondeado para arriba a miles: es un tope. */
export function costoEstimado(c: { reciben: number; compraSemanal: number; uso: number; ticket: number; oferta: number }): number {
  const x = c.reciben * c.compraSemanal * c.uso * c.ticket * (c.oferta / 100);
  if (!Number.isFinite(x) || x <= 0) return 0;
  return Math.ceil(x / 1000) * 1000;
}

export const costoDelGrupo = (g: Pick<GrupoDeMensajes, "reciben" | "compraSemanal" | "uso" | "ticket">, m: Pick<Mensaje, "va" | "canal" | "oferta">) =>
  m.va && m.oferta > 0 ? costoEstimado({ reciben: g.reciben[m.canal], compraSemanal: g.compraSemanal, uso: g.uso.valor, ticket: g.ticket, oferta: m.oferta }) : 0;

/** «$0», o «hasta $12.000» mientras no haya uso del cupón medido. */
export const textoCosto = (n: number, observado: boolean) => (n === 0 ? "$0" : `${observado ? "" : "hasta "}$${num(n)}`);

/* ── El borrador de cada grupo ───────────────────────────────────────────────────────────────── */

export interface Borrador extends Mensaje {
  /** el texto de antes de que la pantalla lo volviera a armar, para «Recuperar mi texto» */
  respaldo: Pick<Mensaje, "asunto" | "texto" | "editado"> | null;
}

/** Lo que la pantalla necesita saber de la marca para armar los textos. */
export interface Contexto {
  tono: TonoMarca;
  vigentes: DescuentoVigente[];
  vigenciaDias: number;
  /** las ofertas que se pueden elegir (con el 0) */
  ofertas: number[];
  compraMinima: number | null;
}

export const contextoDe = (v: VistaDeMensajes): Contexto => ({
  tono: v.marca.tono,
  vigentes: v.descuentos.vigentes,
  vigenciaDias: v.vigenciaDias,
  ofertas: v.descuentos.ofertas,
  compraMinima: v.descuentos.compraMinima,
});

const temperaturaDe = (g: Pick<GrupoDeMensajes, "sugerencia">) => g.sugerencia?.temperatura ?? "hace_mucho";

/** El texto que propone el agente con esa oferta, ese mínimo y esa versión. */
const armado = (g: Pick<GrupoDeMensajes, "sugerencia">, ctx: Contexto, m: Pick<Mensaje, "oferta" | "minimo" | "variante">) =>
  componerMensaje({ temperatura: temperaturaDe(g), tono: ctx.tono, variante: m.variante, oferta: m.oferta, minimo: m.minimo, vigentes: ctx.vigentes, vigenciaDias: ctx.vigenciaDias });

/** La sugerencia del agente; sin sugerencia (o con una oferta que hoy no se puede dar), por mail y sin descuento. */
function sugerida(g: Pick<GrupoDeMensajes, "sugerencia">, ctx: Contexto): Pick<Mensaje, "canal" | "oferta" | "minimo"> {
  const s: Sugerencia | null = g.sugerencia;
  const oferta = s && ctx.ofertas.includes(s.oferta) ? s.oferta : 0;
  return { canal: s?.canal ?? "email", oferta, minimo: oferta > 0 ? (s?.minimo ?? null) : null };
}

/** Con lo guardado, lo guardado; si no, lo que propone el agente. */
export function borradorInicial(g: GrupoDeMensajes, ctx: Contexto): Borrador {
  if (g.guardado) {
    const x = g.guardado;
    return { ...x, cuponId: x.cuponId ?? null, plantillaId: x.plantillaId ?? null, mapeo: x.mapeo ?? null, propuestaIa: x.propuestaIa ?? null, respaldo: null };
  }
  const base = { va: true, ...sugerida(g, ctx), variante: 0, editado: false, cuponId: null, plantillaId: null, mapeo: null, propuestaIa: null };
  return { ...base, ...armado(g, ctx, base), respaldo: null };
}

/** Si el canal, la oferta o el mínimo no son los que propone el agente. */
export function cambioLaSugerencia(g: GrupoDeMensajes, ctx: Contexto, b: Pick<Mensaje, "canal" | "oferta" | "minimo">): boolean {
  if (!g.sugerencia) return false;
  const s = sugerida(g, ctx);
  return b.canal !== s.canal || b.oferta !== s.oferta || (b.oferta > 0 && b.minimo !== s.minimo);
}

/** El texto se vuelve a armar: el que había queda para recuperarlo. Otra oferta u otro mínimo sueltan el cupón. */
function rearmar(g: GrupoDeMensajes, ctx: Contexto, b: Borrador, cambios: Partial<Mensaje>): Borrador {
  const nuevo = { ...b, ...cambios };
  if (nuevo.oferta !== b.oferta || nuevo.minimo !== b.minimo) nuevo.cuponId = null;
  const texto = armado(g, ctx, nuevo);
  const cambiaAlgo = texto.asunto !== b.asunto || texto.texto !== b.texto;
  return { ...nuevo, ...texto, editado: false, respaldo: b.editado && cambiaAlgo ? { asunto: b.asunto, texto: b.texto, editado: true } : b.respaldo };
}

export const cambiarOferta = (g: GrupoDeMensajes, ctx: Contexto, b: Borrador, oferta: number): Borrador =>
  rearmar(g, ctx, b, { oferta, minimo: oferta > 0 ? (b.oferta > 0 ? b.minimo : sugerida(g, ctx).minimo) : null });

export const cambiarMinimo = (g: GrupoDeMensajes, ctx: Contexto, b: Borrador, minimo: number | null): Borrador => rearmar(g, ctx, b, { minimo: b.oferta > 0 ? minimo : null });

export const otraVersion = (g: GrupoDeMensajes, ctx: Contexto, b: Borrador): Borrador => rearmar(g, ctx, b, { variante: (b.variante + 1) % VERSIONES });

export const volverALaSugerencia = (g: GrupoDeMensajes, ctx: Contexto, b: Borrador): Borrador => rearmar(g, ctx, b, sugerida(g, ctx));

/** El canal y si va no tocan el texto. */
export const cambiarCanal = (b: Borrador, canal: Canal): Borrador => ({ ...b, canal });
export const cambiarVa = (b: Borrador, va: boolean): Borrador => ({ ...b, va });

/**
 * «Mandar sólo a este grupo»: ése va y los demás no (la reunión con Llama Leads del 26/10: para mandarle WhatsApp a uno
 * solo de los cuatro, había que apagarlos de a uno). Lo demás de cada borrador queda como estaba.
 */
export const soloEste = <B extends Pick<Mensaje, "va">>(borradores: Record<string, B>, grupoId: string): Record<string, B> =>
  Object.fromEntries(Object.entries(borradores).map(([id, b]) => [id, { ...b, va: id === grupoId }]));

/** «Van los 4 grupos», «Va 1 de 4 grupos», «No va ningún grupo». */
export function textoCuantosVan(van: number, total: number): string {
  if (van === 0) return "No va ningún grupo";
  if (van === total) return total === 1 ? "Va el único grupo" : `Van los ${total} grupos`;
  return `${van === 1 ? "Va" : "Van"} ${van} de ${total} grupos`;
}

/** Escribir a mano: queda editado y ya no hay texto para recuperar. */
export const escribir = (b: Borrador, campo: "asunto" | "texto", valor: string): Borrador => ({ ...b, [campo]: valor, editado: true, respaldo: null });

export const recuperarTexto = (b: Borrador): Borrador => (b.respaldo ? { ...b, ...b.respaldo, respaldo: null } : b);

export const paraGuardar = (grupoId: string, b: Borrador): MensajeParaGuardar => ({
  grupoId,
  va: b.va,
  canal: b.canal,
  oferta: b.oferta,
  minimo: b.oferta > 0 ? b.minimo : null,
  asunto: b.asunto,
  texto: b.texto,
  variante: b.variante,
  editado: b.editado,
  cuponId: b.oferta > 0 ? b.cuponId : null,
  // la plantilla sólo va por WhatsApp (el backend también lo mira)
  plantillaId: b.canal === "whatsapp" ? (b.plantillaId ?? null) : null,
  mapeo: b.canal === "whatsapp" && b.plantillaId ? (b.mapeo ?? {}) : null,
  // la propuesta de «Escribir con IA» sólo va por WhatsApp (el backend también lo mira)
  propuestaIa: b.canal === "whatsapp" ? (b.propuestaIa ?? null) : null,
});

/* ── La plantilla de Meta, por el WhatsApp oficial (R3) ──────────────────────────────────────── */

/** El grupo sale por el WhatsApp oficial: por WhatsApp, con la cuenta oficial de la marca. Ahí no sale el texto. */
export const vaPorOficial = (b: Pick<Mensaje, "canal">, v: Pick<VistaDeMensajes, "whatsapp">) => b.canal === "whatsapp" && !!v.whatsapp.oficial;

/** Otra plantilla borra lo elegido para la anterior: el mapeo nuevo lo arma la pantalla con sus variables. */
export const elegirPlantilla = (b: Borrador, plantillaId: string, mapeo: MapeoVariables | null): Borrador => ({ ...b, plantillaId, mapeo });

export const cambiarMapeo = (b: Borrador, mapeo: MapeoVariables): Borrador => ({ ...b, mapeo });

/**
 * El mapeo de otro grupo que ya usa esa plantilla, para empezar igual: con el mismo mapeo, los dos salen en la misma
 * campaña del oficial (con otro, en dos). null si ningún otro la usa.
 */
export function mapeoDeOtroGrupo(borradores: Record<string, Pick<Mensaje, "canal" | "plantillaId" | "mapeo">>, grupoId: string, plantillaId: string): MapeoVariables | null {
  for (const [id, b] of Object.entries(borradores)) {
    if (id !== grupoId && b.canal === "whatsapp" && b.plantillaId === plantillaId && b.mapeo) return b.mapeo;
  }
  return null;
}

/* ── El cupón de cada grupo (fase 4) ─────────────────────────────────────────────────────────── */

export const elegirCupon = (b: Borrador, cuponId: string | null): Borrador => ({ ...b, cuponId: b.oferta > 0 ? cuponId : null });

/** Es exactamente lo que promete el mensaje: activo, sobre el pedido, mismo % y misma compra mínima, sin vencer. */
export const sirveAlGrupo = (c: CuponDeLaVista, b: Pick<Mensaje, "oferta" | "minimo">) =>
  c.estado === "activo" && c.aplicaA === "pedido" && c.descuentoPct === b.oferta && (c.compraMinima ?? null) === (b.minimo ?? null) && !c.vencido;

/**
 * Los que se pueden elegir para el grupo: los que le sirven y no lleva otro grupo (ni guardado, ni elegido en esta
 * pantalla sin guardar: `elegidos` es cuponId → grupo).
 */
export function cuponesParaElegir(cupones: CuponDeLaVista[], grupoId: string, b: Pick<Mensaje, "oferta" | "minimo">, elegidos: ReadonlyMap<string, string>): CuponDeLaVista[] {
  return cupones.filter((c) => {
    if (!sirveAlGrupo(c, b)) return false;
    if (c.usadoPor && c.usadoPor.grupoId !== grupoId) {
      // de un grupo de esta campaña que en la pantalla ya eligió otro: queda libre al guardar
      const loSuelta = c.usadoPor.grupoId !== null && elegidos.get(c.id) !== c.usadoPor.grupoId;
      if (!loSuelta) return false;
    }
    const otro = elegidos.get(c.id);
    return !otro || otro === grupoId;
  });
}

const fechaCorta = (dia: string) => `${dia.slice(8, 10)}/${dia.slice(5, 7)}`;

/** «vale 7 días desde que le llega» o «del 18/10 al 31/10». */
export const textoVigencia = (c: Pick<CuponDeLaVista, "vigencia">) =>
  c.vigencia.tipo === "dias" ? `vale ${c.vigencia.dias} ${c.vigencia.dias === 1 ? "día" : "días"} desde que le llega` : `del ${fechaCorta(c.vigencia.desde)} al ${fechaCorta(c.vigencia.hasta)}`;

/** Un cupón recién creado desde Mensajes, como lo trae la vista. */
export const cuponDeLaVista = (c: Cupon): CuponDeLaVista => ({
  id: c.id,
  codigo: c.codigo,
  estado: c.estado,
  aplicaA: c.aplicaA,
  descuentoPct: c.descuentoPct,
  compraMinima: c.compraMinima,
  vigencia: c.vigencia,
  vencido: c.vencido,
  empieza: false,
  usadoPor: null,
});

/** Lo que dice la lista de grupos debajo del nombre. */
export const resumenDelGrupo = (b: Pick<Mensaje, "va" | "canal" | "oferta">) =>
  b.va ? `${NOMBRE_CANAL[b.canal]} · ${b.oferta > 0 ? `${b.oferta}%` : "sin descuento"}` : "No va en esta campaña";

/** «El agente sugiere: WhatsApp · 10%». */
export const textoSugerencia = (s: Pick<Sugerencia, "canal" | "oferta">) => `${NOMBRE_CANAL[s.canal]} · ${s.oferta > 0 ? `${s.oferta}%` : "sin descuento"}`;

/* ── El texto de la vista previa ─────────────────────────────────────────────────────────────── */

export const CUPON = "{cupón}";

/** El texto partido donde va el cupón todavía sin elegir, para dibujarlo aparte («elegí el cupón»). */
export function partesConCupon(texto: string): { cupon: boolean; texto: string }[] {
  const out: { cupon: boolean; texto: string }[] = [];
  texto.split(CUPON).forEach((t, i) => {
    if (i > 0) out.push({ cupon: true, texto: CUPON });
    if (t) out.push({ cupon: false, texto: t });
  });
  return out;
}

/* ── El HTML del mail ────────────────────────────────────────────────────────────────────────── */

const BLOQUES = /^<\/?(table|tr|td)\b/i;
const RENGLON = /^<(h1|p)\b/i;

/** El HTML del mail con un renglón por bloque y sangría, para leerlo («Embellecer»). No cambia lo que dice. */
export function embellecerHtml(html: string): string {
  const partes = html.split(/(<[^>]+>)/).filter((p) => p !== "");
  const lineas: string[] = [];
  let nivel = 0;
  let actual = "";
  const cortar = () => {
    if (actual.trim()) lineas.push("  ".repeat(Math.max(0, nivel)) + actual.trim());
    actual = "";
  };
  for (const p of partes) {
    if (BLOQUES.test(p)) {
      cortar();
      if (p.startsWith("</")) {
        nivel--;
        lineas.push("  ".repeat(Math.max(0, nivel)) + p);
      } else {
        lineas.push("  ".repeat(Math.max(0, nivel)) + p);
        nivel++;
      }
    } else if (RENGLON.test(p)) {
      cortar();
      actual = p;
    } else {
      actual += p;
      if (/^<\/(h1|p)>$/i.test(p)) cortar();
    }
  }
  cortar();
  return lineas.join("\n");
}
